import { styled } from "@mui/material/styles";
import {
    Avatar,
    Box,
    Button,
    CardActions,
    CardHeader,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import CardContent from "@mui/material/CardContent";
import { BUTTONS_BASE_STYLES } from "@shared/styles";
import { TIPPSPIEL_BOX_SHADOW } from "../../../styles";

export enum PostCardType {
    normal,
    highlighted,
    active,
}

export const CardBoxed = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isBoxed" && prop !== "type",
})<{ isBoxed?: boolean; type?: PostCardType }>(({
    isBoxed,
    type = PostCardType.normal,
}) => {
    let boxShadow = TIPPSPIEL_BOX_SHADOW;
    let border = "unset";
    switch (type) {
        case PostCardType.active:
            boxShadow = "#50739d80 0px 5px 15px";
            border = "1px solid var(--community-active-game-in-progress)";
            break;
        case PostCardType.highlighted:
            boxShadow = "#50739d80 0px 5px 15px";
            border = "1px solid #50739d";
            break;
    }

    return isBoxed
        ? {
              cursor: "pointer",
              width: "auto",
              padding: "12px",
              margin: "0px 0px 16px",
              borderRadius: "12px",
              backgroundColor: "var(--community-color-snow-white)",
              boxShadow: boxShadow,
              transition: "box-shadow 0.3s ease-in-out",
              WebkitTapHighlightColor: "transparent",
              ".MuiCardContent-root:last-child": {
                  paddingBottom: 0,
              },
              border: border,
          }
        : {
              width: "auto",
              boxShadow: "unset",
          };
});

export const CardHeaderPostHeader = styled(CardHeader)({
    alignItems: "flex-start",
    width: "100%",
    padding: "0",
    "& .MuiCardHeader-action": {
        margin: 0,
    },
    "& .MuiCardHeader-content": {
        overflow: "hidden",
    },
    "& .MuiCardHeader-avatar": {
        marginRight: "12px",
    },
});

export const PostCardHeaderTitleSection = styled(Box)({
    display: "flex",
    alignItems: "center",
});

export const CardContentPost = styled(CardContent)({
    marginTop: "12px",
    padding: 0,
});

export const AvatarUserPostAvatar = styled(Avatar, {
    shouldForwardProp: (prop) => prop !== "backgroundColor",
})<{ backgroundColor?: string }>(({ backgroundColor }) => ({
    backgroundColor:
        backgroundColor ?? "var(--community-avatar-background-color)",
}));

export const PostOptionsButton = styled(IconButton)({
    svg: { fontSize: "24px" },
});
export const TypographyPostHeaderTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: "1.5",
    cursor: "pointer",
    color: "var(--community-color-primary-text)",
});

export const TypographyHeaderTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: "24px",
    cursor: "pointer",
});

export const TypographyHeaderSubTitle = styled(Box)({
    fontSize: "12px",
    color: "var(--community-color-secondary-text)",
    lineHeight: 1.5,
});

export const TypographyHeaderSubTitleBox = styled(Box)({
    fontSize: "12px",
    color: "var(--community-color-secondary-text)",
    lineHeight: 1.5,
});

export const PostComponentsSection = styled("div")({
    marginTop: "12px",
});

export const PostImageGrid = styled(PostComponentsSection)({
    backgroundColor: "var(--community-color-transparent-black-05)",
    ".grid-container": {
        padding: 0,
        ".cover-text>p": {
            color: "var(--community-color-snow-white)",
            fontSize: "32px",
        },
    },
    img: {
        objectFit: "cover",
        maxHeight: "80vh",
    },
});

export const TypographyCardContent = styled(Typography)({
    color: "var(--community-color-primary-text)",
    display: "inline",
    width: "100%",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "pre-wrap",
});

export const TypographyPostLocation = styled(Typography)({
    color: "var(--community-color-primary-text)",
    display: "inline",
    width: "100%",
    fontSize: "14px",
    lineHeight: "20px",
});

export const CardActionsPostActions = styled(CardActions)({
    padding: "0",
    marginTop: "12px",
});

export const PostCardContentDivider = styled(Divider)({
    margin: "12px 0",
});

export const PostPreviewSectionFooterText = styled(Typography)({
    fontSize: "14px",
    fontWeight: 400,
    color: "var(--community-color-bright-azure)",
    marginTop: "12px",
    width: "100%",
    textAlign: "right",
});
export const PostNoCommentsFooterHeadline = styled(Typography)({
    fontSize: "14px",
    color: "var(--community-color-primary-text)",
});

export const PostCardPrimaryActionButton = styled(Button)({
    ...BUTTONS_BASE_STYLES,
    lineHeight: "1.5",
    color: "var(--community-color-primary-text)",
    alignSelf: "center",
    textTransform: "none",
    transition: "box-shadow 0.3s ease-in-out",
    backgroundColor: "var(--community-action-buttons-background-color)",
    "&:hover": {
        backgroundColor: "var(--community-action-buttons-background-color)",
    },
    "& .MuiButton-endIcon": {
        marginLeft: "8px",
        marginRight: "0",
        display: "flex",
        alignItems: "center",
        svg: {
            fontSize: "unset",
        },
    },
});

export const GroupSpan = styled("span")({
    color: "var(--community-color-primary-text)",
    fontWeight: "700",
});

export const PostCardDivider = styled(Divider)({
    margin: "8px -16px 8px",
});

export const ReactedUsersBox = styled(Box)({
    display: "flex",
    "& .MuiBadge-root": {
        "&:not(:last-of-type)": {
            marginRight: "8px",
        },
        svg: {
            width: "16px",
            height: "16px",
        },
    },
});

export const TextNonBreakable = styled("span")({
    whiteSpace: "nowrap",
    display: "inline",
});

export const TypographyClickable = styled(Typography)({
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "700",
    color: "var(--community-color-primary-text)",
    display: "inline",
});

export const HighlightedBox = styled(Box)({
    backgroundColor: "var(--community-color-light-steal-blue)",
    padding: "20px 8px 8px 8px",
    width: "40px",
    height: "52px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
    marginTop: "-12px",
    marginLeft: "8px",
});

export const PostCommentPreviewContainer = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== "isVisible" && prop !== "actualHeight",
})<{ isVisible: boolean; actualHeight: string }>(
    ({ isVisible, actualHeight }) => ({
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        height: isVisible ? `${actualHeight}px` : "0",
        transition:
            "opacity 0.5s ease, transform 0.5s ease, visibility 0.5s ease, height 0.5s ease-in-out",
        overflow: "hidden",
    }),
);

export const CreateCommentTeaserContainer = styled(Box)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "8px",
    flex: "1",
    flexDirection: "row",
});

export const CreateCommentTeaserTextBox = styled(Box)({
    backgroundColor: "var(--community-color-transparent-black-05)",
    borderRadius: "8px",
    padding: "4px",
    display: "flex",
    color: "var(--community-color-secondary-text)",
    fontSize: "14px",
    flex: 1,
    height: "32px",
    alignItems: "center",
});

export const CustomizedImageItem = styled(Box)<{ image?: string }>(
    ({ image }) => ({
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "var(--community-color-transparent-black-05)",
        backgroundImage: `url(${image})`,
        borderRadius: "4px",
        padding: "24px 16px",
    }),
);

export const CounterUnitTypography = styled(Typography)({
    color: "#FFF",
    fontSize: "1.25rem",
    fontWeight: 700,
    lineHeight: "1",
});

export const CounterLabelTypography = styled(Typography)({
    color: "#FFF",
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "1",
});

export const ImageLabelTypography = styled(Typography)({
    color: "#FFF",
    fontSize: "1.25rem",
    fontWeight: 700,
});
